// OPTIMIZATION: Use centralized Parse initialization
import Parse from './initializeParse'
import { UserProfile, AuthUser, AuthResponse } from '../core/models/User'
import { notifyProfileObservers } from '../account/profileEvents'
import { resolveAvatarSource, withAvatarCacheBust } from '../account/accountProfileMappers'
import { CompanyService } from './CompanyService'
import { trackAuthSuccess, type TrackAuthSuccessOptions } from './analyticsService'
import { accountStatusService } from './AccountStatusService'
import { usePermissionStore } from '../stores/permissionStore'
import { clearCompanyAccessState } from '../stores/companyAccessStore'
import { clearRbacState } from '../stores/rbacStore'
import { clearPendingCompanyInvite } from './CompanyInviteLinkService'
import { clearWorkspaceLandingHandled, clearWorkspaceEnteredSession } from '../components/MainMenu/utils/mainMenuLanding'
import { clearSession as clearChatSession } from '../components/ChatPanel/backend/matrix/session'
import { releaseAllBackgroundImageUrls } from './backgroundLibrary'
import type { SignupConsent } from '../consent/types'
import { readGoogleOAuthSignupConsent } from '../utils/googleOAuthClient'
import { isInvalidSessionError, SESSION_EXPIRED_MESSAGE } from '../utils/sessionErrors'
import { phaserEvents, Event as GameEvent } from '../events/EventCenter'

export interface AuthError {
  code: number
  message: string
  field?: string
}

/**
 * Sessions last 30 days and signing in elsewhere deletes the others, so a tab left open learns it
 * was signed out only by asking Parse. Asking more often than this costs Back4App requests for no
 * gain.
 */
const SESSION_RECHECK_MIN_GAP_MS = 60_000

/**
 * A `become` that hangs on a half-open connection must not pin the light check for minutes; the
 * caller gets the user we hold (never null, which would read as signed out) after this long.
 */
const LIGHT_CHECK_TIMEOUT_MS = 10_000

/**
 * What `checkSession()` resolves to when it stopped waiting for a slow Back4App. Kept apart from
 * `null` ("no session") so a caller that must not treat slowness as a sign-out can tell them apart.
 */
const SESSION_CHECK_TIMED_OUT = Symbol('sessionCheckTimedOut')
type SessionCheckOutcome = AuthUser | null | typeof SESSION_CHECK_TIMED_OUT

/**
 * A page that is really being reloaded is gone well before this. Still running after it means the
 * reload was held back (a leave prompt answered with "Stay", a blocked navigation), so the account
 * switch has to be armed again.
 */
const RELOAD_HELD_BACK_AFTER_MS = 10_000

/**
 * What the cloud's company check answers (`verifyUserCompanyAssignment`'s reply), as the page-load
 * call `checkSessionAndCompany` and `enhancedSignIn` carry it in `company`. `id` and `name` are the
 * other shape a sign-up reply gives its `company`; sign-in reads both.
 */
interface CompanyCheckReply {
  id?: string
  name?: string
  repairs?: { type?: string }[]
  inconsistencies?: unknown[]
  finalState?: { companyId?: string | null }
}

/**
 * A company check that found (or repaired in) a company makes a cached "not associated with any
 * company" answer wrong, and the 5 min cache would keep showing it. A check that finds nothing,
 * and a cached company, are left alone, so the normal path makes no extra read.
 */
function noteCompanyCheck(check: CompanyCheckReply | null | undefined): void {
  if (!check) return
  const resolved =
    !!check.id || !!check.finalState?.companyId || (check.repairs?.length ?? 0) > 0
  if (resolved) CompanyService.dropNoCompanyEntry()
}

/**
 * True when Parse Server says the cloud has no such function. The cloud is uploaded by hand, so a
 * new client can meet one that predates `checkSessionAndCompany`.
 *
 * Parse also answers 141 for any non-Parse error thrown inside a function that does exist, so the
 * code alone would send a real failure down the fallback. Only the "Invalid function" text of the
 * missing-function case tells the two apart. (`consent/consentService.ts` has the same test; it
 * cannot be imported here, because its module chain imports this one.)
 */
function isMissingCloudFunction(error: unknown): boolean {
  const e = error as { code?: unknown; message?: unknown } | null | undefined
  return e?.code === 141 && typeof e.message === 'string' && /invalid function/i.test(e.message)
}

/**
 * Enhanced Authentication Service following Back4App best practices
 * - Uses Parse SDK properly with session tokens
 * - Ensures profile data is always loaded and available
 * - Eliminates race conditions in authentication flow
 * - Follows Back4App recommended patterns
 */
class Back4AppAuthService {
  private readonly STORAGE_KEY = 'wiggin_office_auth'
  private currentUser: AuthUser | null = null
  private currentProfile: UserProfile | null = null
  private sessionCheckPromise: Promise<SessionCheckOutcome> | null = null
  /** Cached result of the first boot-time session check; hooks await this gate. */
  private sessionReadyPromise: Promise<AuthUser | null> | null = null
  /**
   * The full check (become + checkSessionAndCompany) itself, kept apart from
   * `sessionCheckPromise` because that one is a race against a timeout: a check that lost the
   * race is still running, and the next caller must join it rather than start another.
   */
  private fullCheckInFlight: Promise<AuthUser | null> | null = null
  /**
   * Set once the cloud answered "Invalid function" for `checkSessionAndCompany` (it has not been
   * uploaded yet). Every later full check in this tab goes straight to the old pair
   * (validateSession + verifyUserCompanyAssignment) instead of paying a request that fails again.
   * Not cleared by sign-out: what the cloud has deployed does not depend on the user.
   */
  private combinedCheckMissing = false
  private lightCheckPromise: Promise<AuthUser | null> | null = null
  /**
   * True once a full check (or a sign-in, which does the same work) has succeeded in this page.
   * Later `checkSession()` callers reuse `currentUser` instead of asking Back4App again. Cleared
   * by sign-out, a new sign-in and the `sessionExpired` event.
   */
  private sessionResultRemembered = false
  /** When Parse last answered (or was last asked) about the stored token, for the recheck gap. */
  private lastSessionVerifiedAt = 0
  /**
   * Bumped whenever sign-out or a new sign-in invalidates the checks. A check that started
   * earlier compares it before writing anything, so it cannot sign out or re-remember the
   * wrong session.
   */
  private sessionEpoch = 0
  /**
   * The last token Parse refused (209) while the shared storage already held a newer one. It is
   * dead for good, so no save of the in-memory user may put it back over the newer token, which
   * the other tab (and this one after a reload) still needs.
   */
  private refusedSessionToken: string | undefined
  /**
   * Another tab signed in as a different account and this tab is only waiting to reload. While
   * set, the light check stands still: asking Parse again could only switch the SDK user further
   * under the account the app still shows.
   */
  private accountSwitchPending = false
  /** Reloads the page once the player has left the room; kept so a sign-out can withdraw it. */
  private accountSwitchReload: (() => void) | null = null
  /** Re-arms the account switch if a reload it started never took the page away. */
  private reloadHeldBackTimer: ReturnType<typeof setTimeout> | undefined

  constructor() {
    // Parse is initialized via the centralized initializeParse module
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      // parseCloud fires this after a cloud call came back 209/206, possibly without signing
      // out (a player in a room stays). Whatever we remember is no longer trustworthy.
      window.addEventListener('sessionExpired', () => this.forgetSessionResult())
    }
  }

  /**
   * Notify server of stable device id after sign-in so it can send login-new-device email when needed.
   */
  private async notifySecurityDeviceCheck(sessionToken: string | undefined): Promise<void> {
    if (!sessionToken) return
    try {
      const { getOrCreateAuthDeviceId } = await import('../utils/authDeviceId')
      const { getServerBaseUrl } = await import('../utils/serverUrl')
      const deviceId = getOrCreateAuthDeviceId()
      const deviceInfo =
        typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
          ? navigator.userAgent.slice(0, 512)
          : ''
      const baseUrl = getServerBaseUrl().replace(/\/$/, '')
      const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
      // Relative /api only works in a real browser (Vite proxy). Node/Vitest needs an absolute URL.
      const useRelativeDev =
        isDev &&
        typeof window !== 'undefined' &&
        typeof (window as any).location?.origin === 'string'
      const url = useRelativeDev
        ? '/api/auth/security/device-check'
        : `${baseUrl}/api/auth/security/device-check`
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`,
        },
        credentials: useRelativeDev ? 'same-origin' : 'include',
        body: JSON.stringify({ deviceId, deviceInfo }),
      })
    } catch (e) {
      console.warn('[AuthService] security device-check failed (non-blocking):', e)
    }
  }

  /**
   * Sign up new user following Back4App best practices
   * Creates user and profile in a single transaction
   *
   * `consent` is the Create Account marketing choice (SC-360). It is a top-level parameter
   * because cloud reads `profileData` field by field and would drop it. Omitted when not
   * given, so an older cloud sees exactly the payload it always did.
   */
  async signUp(
    username: string,
    email: string,
    password: string,
    profileData: any = {},
    consent?: SignupConsent
  ): Promise<AuthResponse> {
    try {
      this.invalidateSessionChecks()
      // Clear any existing session before signup
      try {
        await this.clearParseSession()
      } catch (logoutError) {
        // Ignore logout errors
      }

      // Use cloud function for atomic user + profile creation
      const response = await Parse.Cloud.run('enhancedSignUp', {
        username,
        email,
        password,
        profileData: {
          avatar: profileData.avatar || 'adam',
          gameCharacter: profileData.gameCharacter || 'adam',
          firstName: profileData.firstName || '',
          lastName: profileData.lastName || '',
          role: 'user'
        },
        ...(consent ? { consent } : {})
      })

      // CRITICAL FIX: Ensure Parse session is properly established with user session token
      if (response.user?.sessionToken) {
        try {
          // Use Parse.User.become to establish the session with the user's token
          const user = await Parse.User.become(response.user.sessionToken)
          console.log('[AuthService] Session established for new user:', user.id)
        } catch (sessionError) {
          console.error('[AuthService] Failed to establish session with token:', sessionError)
          // Fallback: try to log in with credentials to get fresh session
          try {
            const loginUser = await Parse.User.logIn(email, password)
            console.log('[AuthService] Fallback login successful:', loginUser.id)
            // Update the session token in response
            response.user.sessionToken = loginUser.getSessionToken()
          } catch (loginError) {
            console.error('[AuthService] Fallback login also failed:', loginError)
            throw new Error('Failed to establish user session after registration')
          }
        }
      } else {
        // No session token returned, try to log in to get one
        try {
          const loginUser = await Parse.User.logIn(email, password)
          console.log('[AuthService] Login after registration successful:', loginUser.id)
          response.user.sessionToken = loginUser.getSessionToken()
        } catch (loginError) {
          console.error('[AuthService] Login after registration failed:', loginError)
          throw new Error('Failed to establish user session after registration')
        }
      }

      const authResponse: AuthResponse = {
        user: {
          ...response.user,
          sessionToken: response.user.sessionToken
        },
        profile: response.profile
      }

      // Store in memory and localStorage
      this.currentUser = authResponse.user
      this.currentProfile = authResponse.profile
      this.storeAuthData(authResponse.user, authResponse.profile, authResponse.user.sessionToken)
      const signupCompany = (response as { company?: { id?: string; name?: string } }).company
      const regTrack: TrackAuthSuccessOptions = {
        authFlow: 'registration',
        authMethod: 'password',
        email: authResponse.user.email || email,
        username: authResponse.user.username || username,
      }
      if (signupCompany?.id) {
        regTrack.companyAssigned = true
        regTrack.companyId = signupCompany.id
        if (signupCompany.name) regTrack.companyName = signupCompany.name
      }
      trackAuthSuccess(regTrack)

      // Verify session is properly established
      const currentUser = Parse.User.current()
      if (!currentUser || !currentUser.getSessionToken()) {
        console.error('[AuthService] Session verification failed after signup')
        throw new Error('Session establishment failed after registration')
      }

      // Check if user was assigned to a company and requires billing
      if (response.company && response.company.requiresBilling) {
        console.log('[AuthService] User assigned to company, processing billing:', response.company.name)
        try {
          await this.processCompanyBilling(response.company.id, authResponse.user.id, email)
        } catch (billingError) {
          console.error('[AuthService] Billing processing failed:', billingError)
          // Don't fail the registration if billing fails
        }
      }

      void this.notifySecurityDeviceCheck(authResponse.user.sessionToken)

      this.resolveSessionReady(this.currentUser)
      console.log('[AuthService] Signup completed successfully with session token')
      return authResponse

    } catch (error: any) {
      throw this.formatError(error)
    }
  }

  /**
   * Detect error type for better error handling
   */
  private detectErrorType(error: any): 'NOT_FOUND' | 'NETWORK' | 'AUTHENTICATION' | 'VALIDATION' | 'OTHER' {
    // 155 is cloud's sign-in/sign-up rate limiter saying "stop". Retrying it would only burn
    // more of the user's allowance, and the fallback would bypass the limiter, so it is
    // classified as a final answer before any of the looser checks below. [SC-409]
    if (error?.code === Parse.Error.REQUEST_LIMIT_EXCEEDED) {
      return 'AUTHENTICATION'
    }

    // 205 is cloud's definitive "no account with this email" answer. Checked first so the
    // loose "not found" text matching below can never turn it into a retry or into the
    // "cloud function unavailable" fallback. [SC-409]
    if (error?.code === Parse.Error.EMAIL_NOT_FOUND) {
      return 'AUTHENTICATION'
    }

    // Check for 404 or function not found errors
    // Handle both Parse errors and HTTP errors
    if (error.code === 141 || // Parse.Error.FUNCTION_NOT_FOUND
      error.code === 404 ||
      error.status === 404 ||
      (error.message && (error.message.includes('404') || error.message.includes('Not Found'))) ||
      (error.message && error.message.toLowerCase().includes('not found')) ||
      (error.response && error.response.status === 404) ||
      (String(error).includes('404'))) {
      return 'NOT_FOUND'
    }

    // Check for network errors
    if (error.code === 100 || // Parse.Error.CONNECTION_FAILED
      error.code === 107 || // Parse.Error.TIMEOUT
      error.message?.includes('network') ||
      error.message?.includes('timeout') ||
      error.message?.includes('fetch') ||
      error.message?.includes('Failed to fetch') ||
      error.name === 'NetworkError' ||
      (error.response && error.response.status >= 500)) {
      return 'NETWORK'
    }

    // Check for authentication errors
    if (error.code === 101 || // Parse.Error.OBJECT_NOT_FOUND (used for invalid login)
      error.code === 200 || // Parse.Error.USERNAME_MISSING
      error.code === 201 || // Parse.Error.PASSWORD_MISSING
      error.message?.includes('Invalid username/password') ||
      error.message?.includes('authentication')) {
      return 'AUTHENTICATION'
    }

    // Check for validation errors
    if (error.code === 142 || // Parse.Error.INVALID_JSON
      error.code === 600 || // Parse.Error.INVALID_QUERY
      error.message?.includes('validation') ||
      error.message?.includes('required')) {
      return 'VALIDATION'
    }

    return 'OTHER'
  }

  /**
   * enhancedSignIn's answers that come from a working cloud function and describe the account,
   * not an outage: an unknown email (205), a rate-limit refusal (155), and a suspended or locked
   * account (119, which enhancedSignIn throws for nothing else). Retrying any of them returns the
   * same answer while spending more of the per-email sign-in allowance, and the direct-Parse
   * fallback would sidestep the cloud's checks and its limiter. Kept out of the shared
   * detectErrorType because other paths see 119 for other reasons (for example the fallback's
   * refused `_User` email query). [SC-409]
   */
  private isDefinitiveSignInAnswer(error: any): boolean {
    return (
      error?.code === Parse.Error.EMAIL_NOT_FOUND ||
      error?.code === Parse.Error.REQUEST_LIMIT_EXCEEDED ||
      error?.code === Parse.Error.OPERATION_FORBIDDEN
    )
  }

  /**
   * Retry function with exponential backoff
   */
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    options: { maxRetries: number; context?: any; isFinal?: (error: any) => boolean }
  ): Promise<T> {
    const { maxRetries, context, isFinal } = options
    let lastError: any = null

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn()
      } catch (error: any) {
        lastError = error
        const errorType = this.detectErrorType(error)

        // Don't retry authentication or validation errors, or anything the caller knows is final
        if (errorType === 'AUTHENTICATION' || errorType === 'VALIDATION' || isFinal?.(error)) {
          throw error
        }

        // Don't retry if this was the last attempt
        if (attempt >= maxRetries) {
          break
        }

        // Calculate backoff delay (exponential: 100ms, 200ms, 400ms...)
        const delay = Math.min(100 * Math.pow(2, attempt), 1000)
        console.log(`[AuthService] Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`, context)

        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    throw lastError
  }

  /**
   * Fallback sign in using direct Parse.User.logIn
   * Used when cloud function is unavailable
   */
  private async signInWithFallback(
    email: string,
    password: string,
    context: any
  ): Promise<any> {
    console.log('[AuthService] Using fallback sign in method', context)

    try {
      // Normalize email for lookup (case-insensitive)
      const normalizedEmail = String(email || '').trim().toLowerCase()

      // Try to find user by email first (case-insensitive)
      const userQuery = new Parse.Query(Parse.User)
      const escaped = normalizedEmail.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      userQuery.matches('email', `^${escaped}$`, 'i')

      // Parse Server 6+ always refuses this lookup for an anonymous caller: `email` is a
      // protected field on _User, so it rejects with 119 "This user is not allowed to query
      // email on class _User". Only enhancedSignIn (master key) can tell whether an email has
      // an account, so this path must never claim it has none, and never show Parse's raw
      // text. A refusal means this fallback can't stand in for the missing cloud function,
      // which is an outage; a network failure keeps its own "check your connection" message.
      // [SC-409]
      let foundUser: Parse.User | undefined
      try {
        foundUser = await userQuery.first()
      } catch (lookupError: any) {
        if (this.detectErrorType(lookupError) === 'NETWORK') {
          throw lookupError
        }
        console.warn('[AuthService] Fallback email lookup refused', {
          code: lookupError?.code,
          message: lookupError?.message,
          ...context,
        })
        // 141 is the code this service and the dialog already treat as "sign-in function
        // unavailable", so formatError and signInErrorView show the service-unavailable toast.
        throw new Parse.Error(141, 'Authentication service temporarily unavailable.')
      }

      if (!foundUser) {
        // Unreachable on Parse Server 6+ (see above), but if a server ever answers, an empty
        // result still doesn't prove the email is unknown. Report it as the generic "incorrect
        // email or password". A plain Error, not a Parse.Error, so formatError leaves the field
        // as 'general' instead of pinning it on the password; signInErrorView then picks the
        // credentials copy for a 101 that has no field.
        throw Object.assign(new Error('Invalid username/password.'), {
          code: Parse.Error.OBJECT_NOT_FOUND,
        })
      }

      // Log in with username (more reliable than email)
      const user = await Parse.User.logIn(foundUser.get('username'), password)
      console.log('[AuthService] Fallback login successful:', user.id)

      // Get session token
      const sessionToken = user.getSessionToken()
      if (!sessionToken) {
        throw new Error('No session token received from login')
      }

      // Load profile using fallback method
      const profile = await this.loadProfileWithFallback(user.id, context)

      return {
        user: {
          id: user.id,
          username: user.get('username'),
          email: user.get('email'),
          sessionToken: sessionToken,
          createdAt: user.createdAt
        },
        profile: profile
      }
    } catch (error: any) {
      console.error('[AuthService] Fallback sign in failed', {
        error: error.message,
        code: error.code,
        ...context
      })
      throw error
    }
  }

  /**
   * Load profile using direct Parse query (fallback when cloud function unavailable)
   */
  private async loadProfileWithFallback(userId: string, context: any): Promise<UserProfile | null> {
    try {
      console.log('[AuthService] Loading profile via fallback method', { userId, ...context })

      const Profile = Parse.Object.extend('Profile')
      const query = new Parse.Query(Profile)
      query.equalTo('userId', userId)

      const profile = await query.first()

      if (!profile) {
        console.warn('[AuthService] No profile found for user', { userId, ...context })
        return null
      }

      // Map to UserProfile format
      const mappedProfile: UserProfile = {
        firstName: profile.get('firstName') || '',
        lastName: profile.get('lastName') || '',
        displayName: profile.get('displayName') || '',
        title: profile.get('title') || '',
        bio: profile.get('bio') || '',
        contact: profile.get('contact') || '',
        avatar: profile.get('avatar') || 'adam',
        customAvatarUrl: profile.get('customAvatarUrl') || '',
        gameCharacter: profile.get('gameCharacter') || 'adam',
        role: profile.get('role') || 'user',
        profileCompleted: profile.get('profileCompleted') ?? false,
        gender: profile.get('gender') || '',
        dateOfBirth: profile.get('dateOfBirth') || undefined,
        avatarSource: profile.get('avatarSource') || undefined,
        avatarUpdatedAt: profile.get('avatarUpdatedAt') || undefined,
      }

      console.log('[AuthService] Profile loaded via fallback', {
        userId,
        firstName: mappedProfile.firstName,
        lastName: mappedProfile.lastName,
        ...context
      })

      return mappedProfile
    } catch (error: any) {
      console.error('[AuthService] Fallback profile loading failed', {
        userId,
        error: error.message,
        ...context
      })
      return null
    }
  }

  /**
   * Sign in user following Back4App best practices
   * Uses cloud function for optimized session management with robust fallback
   */
  async signIn(email: string, password: string): Promise<AuthResponse> {
    const context = {
      email: email.substring(0, 3) + '***',
      timestamp: new Date().toISOString(),
      attempt: 1
    }

    console.log('[AuthService] Starting sign in process', context)

    try {
      this.invalidateSessionChecks()
      // Clear any existing (possibly invalid) Parse session before signing in
      try {
        await this.clearParseSession()
      } catch (logoutError) {
        // Ignore logout errors
      }

      // Try cloud function with retry logic
      let response: any = null
      let useFallback = false

      try {
        response = await this.retryWithBackoff(
          () => Parse.Cloud.run('enhancedSignIn', {
            email,
            password,
          }),
          {
            maxRetries: 2,
            context: { ...context, operation: 'enhancedSignIn' },
            isFinal: (error) => this.isDefinitiveSignInAnswer(error),
          }
        )
        console.log('[AuthService] Cloud function sign in successful', { userId: response.user?.id })
      } catch (cloudError: any) {
        // Enhanced error logging for debugging
        console.warn('[AuthService] Cloud function sign in failed', {
          error: cloudError,
          errorString: String(cloudError),
          errorJSON: JSON.stringify(cloudError),
          code: cloudError?.code,
          message: cloudError?.message,
          status: cloudError?.status,
          response: cloudError?.response
        })

        const errorType = this.detectErrorType(cloudError)
        console.warn('[AuthService] Detected error type', {
          errorType,
          code: cloudError?.code,
          message: cloudError?.message
        })

        // Use fallback for 404 (function not found) or network errors
        // Also check for HTTP 404 status codes
        // If error object is empty or malformed, also use fallback as safety measure
        // A definitive answer (205, 155, 119) is not an outage. The fallback signs in with
        // Parse.User.logIn directly, so running it after a 155 or 119 would sidestep the cloud.
        const isDefinitiveCloudAnswer = this.isDefinitiveSignInAnswer(cloudError)
        const is404Error = !isDefinitiveCloudAnswer && (errorType === 'NOT_FOUND' ||
          errorType === 'NETWORK' ||
          cloudError?.status === 404 ||
          cloudError?.code === 141 ||
          (cloudError?.message && (cloudError.message.includes('404') || cloudError.message.includes('not found'))) ||
          (!cloudError?.code && !cloudError?.message && !cloudError?.status)) // Empty/malformed error

        if (is404Error) {
          console.log('[AuthService] Using fallback authentication method due to', {
            errorType,
            status: cloudError?.status,
            code: cloudError?.code,
            hasMessage: !!cloudError?.message,
            errorObject: cloudError
          })
          useFallback = true
        } else {
          // For authentication errors, don't retry - return immediately
          throw cloudError
        }
      }

      // Use fallback authentication if cloud function failed
      if (useFallback || !response) {
        response = await this.signInWithFallback(email, password, context)
      }

      // CRITICAL FIX: Ensure Parse session is properly established with user session token
      if (response.user?.sessionToken) {
        try {
          // Use Parse.User.become to establish the session with the user's token
          const user = await Parse.User.become(response.user.sessionToken)
          console.log('[AuthService] Session established for existing user:', user.id)
        } catch (sessionError) {
          console.error('[AuthService] Failed to establish session with token:', sessionError)
          // Fallback: try to log in with credentials to get fresh session
          try {
            const loginUser = await Parse.User.logIn(email, password)
            console.log('[AuthService] Fallback login successful:', loginUser.id)
            // Update the session token in response
            response.user.sessionToken = loginUser.getSessionToken()
          } catch (loginError) {
            console.error('[AuthService] Fallback login also failed:', loginError)
            throw new Error('Failed to establish user session during sign in')
          }
        }
      } else {
        // No session token returned, try to log in to get one
        try {
          const loginUser = await Parse.User.logIn(email, password)
          console.log('[AuthService] Login successful:', loginUser.id)
          response.user.sessionToken = loginUser.getSessionToken()
        } catch (loginError) {
          console.error('[AuthService] Login failed:', loginError)
          throw new Error('Failed to establish user session during sign in')
        }
      }

      const authResponse: AuthResponse = {
        user: {
          ...response.user,
          sessionToken: response.user.sessionToken
        },
        profile: response.profile
      }

      // Store in memory and localStorage
      this.currentUser = authResponse.user
      this.currentProfile = authResponse.profile
      this.storeAuthData(authResponse.user, authResponse.profile, authResponse.user.sessionToken)

      // Verify session is properly established
      const currentUser = Parse.User.current()
      if (!currentUser || !currentUser.getSessionToken()) {
        console.error('[AuthService] Session verification failed after signin')
        throw new Error('Session establishment failed during sign in')
      }

      const loginCompany = (response as { company?: CompanyCheckReply | null }).company
      // The cloud's `enhancedSignIn` runs the company check itself and returns it as `company`, so
      // running it again here only repeats its 4-5 requests (the analytics fields below are all
      // this call is for). An older cloud returns no `company`, and null means its check threw: in
      // both cases the check still runs here.
      const signInCompanyCheck =
        loginCompany !== null && typeof loginCompany === 'object' ? loginCompany : null
      let companyAssigned: boolean | undefined
      let companyId: string | null | undefined
      let companyName: string | null | undefined

      try {
        const verificationResult =
          signInCompanyCheck ??
          ((await Parse.Cloud.run('verifyUserCompanyAssignment', {})) as CompanyCheckReply)
        noteCompanyCheck(verificationResult)

        if (loginCompany?.id) {
          companyAssigned = true
          companyId = loginCompany.id
          companyName = loginCompany.name ?? null
        } else {
          const cid = verificationResult?.finalState?.companyId
          if (cid) {
            companyAssigned = true
            companyId = String(cid)
            companyName = null
          }
        }

        if (verificationResult.repairs && verificationResult.repairs.length > 0) {
          console.log('[AuthService] Company assignment repairs applied after login:', {
            userId: authResponse.user.id,
            repairs: verificationResult.repairs.map((r: any) => r.type)
          })
        }
        if (verificationResult.inconsistencies && verificationResult.inconsistencies.length > 0) {
          console.log('[AuthService] Company assignment inconsistencies found (may have been repaired):', {
            userId: authResponse.user.id,
            inconsistencies: verificationResult.inconsistencies.length
          })
        }
      } catch (verifyError: any) {
        noteCompanyCheck(loginCompany)
        if (loginCompany?.id) {
          companyAssigned = true
          companyId = loginCompany.id
          companyName = loginCompany.name ?? null
        }
        console.warn('[AuthService] Company assignment verification failed (non-blocking):', {
          userId: authResponse.user.id,
          error: verifyError.message
        })
      }

      const authTrack: TrackAuthSuccessOptions = {
        authFlow: 'login',
        authMethod: 'password',
        email: authResponse.user.email || email,
        username: authResponse.user.username,
      }
      if (companyAssigned === true) authTrack.companyAssigned = true
      if (companyId) authTrack.companyId = companyId
      if (companyName) authTrack.companyName = companyName
      trackAuthSuccess(authTrack)

      void this.notifySecurityDeviceCheck(authResponse.user.sessionToken)

      this.resolveSessionReady(this.currentUser)
      console.log('[AuthService] Signin completed successfully with session token')
      return authResponse

    } catch (error: any) {
      const errorType = this.detectErrorType(error)
      console.error('[AuthService] Sign in error', {
        errorType,
        code: error.code,
        message: error.message,
        ...context
      })
      throw this.formatError(error, errorType)
    }
  }

  /**
   * App-wide session gate — resolves once per page load (or after signOut).
   * All authenticated cloud calls should await this before running.
   */
  waitForSessionCheck(): Promise<AuthUser | null> {
    if (!this.sessionReadyPromise) {
      const gate: Promise<AuthUser | null> = this.checkSessionOrTimeout().then((outcome) => {
        if (outcome !== SESSION_CHECK_TIMED_OUT) return outcome
        // A slow Back4App is not a sign-out: release the gate so the next caller joins the check
        // that is still running instead of reading this null for the rest of the page.
        if (this.sessionReadyPromise === gate) this.sessionReadyPromise = null
        return null
      })
      this.sessionReadyPromise = gate
    }
    return this.sessionReadyPromise
  }

  /**
   * Forget the remembered check result, so the next `checkSession()` is a full one. Leaves the
   * user and storage alone.
   */
  private forgetSessionResult(): void {
    this.sessionResultRemembered = false
    this.lastSessionVerifiedAt = 0
  }

  /**
   * The signed-in identity is about to change (sign-out, sign-in, sign-up, OAuth, invitation):
   * drop the remembered result and detach every check still in flight, so one that started under
   * the old session can neither sign out nor re-remember the new one.
   */
  private invalidateSessionChecks(): void {
    this.sessionEpoch += 1
    this.forgetSessionResult()
    this.fullCheckInFlight = null
    this.lightCheckPromise = null
    this.sessionCheckPromise = null
    this.cancelAccountSwitchReload()
  }

  /** Reset the boot gate so a fresh check runs after sign-out. */
  private resetSessionReadyGate(): void {
    this.sessionReadyPromise = null
    this.invalidateSessionChecks()
  }

  /**
   * Mark session gate resolved after successful sign-in/up. Those flows have just done the work
   * of a full check, so the result is remembered like one.
   */
  private resolveSessionReady(user: AuthUser | null): void {
    this.sessionReadyPromise = Promise.resolve(user)
    if (user) this.rememberSessionResult()
  }

  private rememberSessionResult(): void {
    this.sessionResultRemembered = true
    this.lastSessionVerifiedAt = Date.now()
  }

  /**
   * Check current session with proper singleton pattern
   * Ensures only one session check runs at a time
   *
   * The full check (become + checkSessionAndCompany, 2 calls) runs once per page load; sign-in
   * counts as one. Later callers reuse its result:
   * AuthenticationController mounting after AppController, AppController remounting when the
   * player leaves the office.
   */
  async checkSession(): Promise<AuthUser | null> {
    const outcome = await this.checkSessionOrTimeout()
    return outcome === SESSION_CHECK_TIMED_OUT ? null : outcome
  }

  private async checkSessionOrTimeout(): Promise<SessionCheckOutcome> {
    if (this.sessionResultRemembered && this.currentUser) {
      return this.currentUser
    }

    // Return existing promise if already checking
    if (this.sessionCheckPromise) {
      return this.sessionCheckPromise
    }

    // Race the session check against a 10-second timeout so a slow or
    // unavailable Back4App connection never blocks the app indefinitely.
    const timeout = new Promise<typeof SESSION_CHECK_TIMED_OUT>((resolve) =>
      setTimeout(() => resolve(SESSION_CHECK_TIMED_OUT), 10000)
    )
    const pending: Promise<SessionCheckOutcome> = Promise.race([this.runFullCheck(), timeout])
    this.sessionCheckPromise = pending

    try {
      return await pending
    } finally {
      if (this.sessionCheckPromise === pending) this.sessionCheckPromise = null
    }
  }

  /** Start the full check, or join the one already running (a timed-out one keeps running). */
  private runFullCheck(): Promise<AuthUser | null> {
    if (!this.fullCheckInFlight) {
      const run: Promise<AuthUser | null> = this.performSessionCheck().finally(() => {
        if (this.fullCheckInFlight === run) this.fullCheckInFlight = null
      })
      this.fullCheckInFlight = run
    }
    return this.fullCheckInFlight
  }

  /**
   * Cheap recheck for a tab that comes back to the foreground: one `Parse.User.become` with the
   * stored token, no validateSession and no verifyUserCompanyAssignment, and at most one per
   * minute however often the tab is switched. It is how a tab left open learns that the session
   * was ended elsewhere (signing in on another device deletes the others).
   *
   * It signs the user out only when Parse answers 209/206, or when the shared storage no longer
   * holds a session (another tab signed out). A network or other error keeps the user as they are.
   * Without a completed full check to build on, it falls back to `checkSession()`.
   *
   * `force` skips the one-minute gap for a caller that holds real evidence the session changed (a
   * cloud call Parse refused with another token in storage); a plain tab return never forces.
   */
  async checkSessionLight({ force = false }: { force?: boolean } = {}): Promise<AuthUser | null> {
    if (this.accountSwitchPending) {
      return this.currentUser
    }

    if (this.lightCheckPromise) {
      return this.lightCheckPromise
    }

    const askedRecently = Date.now() - this.lastSessionVerifiedAt < SESSION_RECHECK_MIN_GAP_MS
    if (askedRecently && this.currentUser && !force) {
      // The minute rations requests to Parse. Comparing the stored account with the held one is a
      // storage read, so another tab's sign-in as someone else is acted on at every tab return.
      return this.switchIfOtherAccount(this.readStoredAuth()?.user?.id) ?? this.currentUser
    }

    if (!this.sessionResultRemembered || !this.currentUser) {
      const outcome = await this.checkSessionOrTimeout()
      // A check that gave up waiting learned nothing about the session. Reading that as "signed
      // out" would sign the user out for a slow connection, as the light check's own timeout below
      // avoids; only Parse's 209/206 do that.
      return outcome === SESSION_CHECK_TIMED_OUT ? this.currentUser : outcome
    }

    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<AuthUser | null>((resolve) => {
      timer = setTimeout(() => resolve(this.currentUser), LIGHT_CHECK_TIMEOUT_MS)
    })
    const run: Promise<AuthUser | null> = Promise.race([this.performLightCheck(), timeout]).finally(
      () => {
        clearTimeout(timer)
        if (this.lightCheckPromise === run) this.lightCheckPromise = null
      }
    )
    this.lightCheckPromise = run
    return run
  }

  private async performLightCheck(): Promise<AuthUser | null> {
    const epoch = this.sessionEpoch

    const stored = this.readStoredAuth()
    const storedToken: string | undefined = stored?.user?.sessionToken

    if (!storedToken) {
      // Sign-out clears the storage every tab shares, so another tab ended this session.
      await this.endSessionLocally()
      return null
    }

    // Another tab signed in as someone else. Asking Parse would switch this tab's SDK user
    // under an app still showing the old account, so start over as the new one instead.
    const switchingFromStored = this.switchIfOtherAccount(stored?.user?.id)
    if (switchingFromStored) return switchingFromStored

    try {
      this.lastSessionVerifiedAt = Date.now()
      const user = await Parse.User.become(storedToken)
      if (epoch !== this.sessionEpoch) return this.currentUser

      const switchingFromParse = this.switchIfOtherAccount(user?.id)
      if (switchingFromParse) return switchingFromParse

      // The token another tab stored (a password change or a new sign-in revokes the old one) is
      // the one that works. Later writes of the in-memory user to storage must not bring the
      // revoked token back.
      const liveToken = user?.getSessionToken?.()
      if (this.currentUser && liveToken && this.currentUser.sessionToken !== liveToken) {
        this.currentUser.sessionToken = liveToken
      }
      return this.currentUser
    } catch (error) {
      return this.handleSessionCheckFailure(error, epoch, null, storedToken)
    }
  }

  /**
   * `otherUserId` (the shared storage's, or the one Parse answered with) names another account
   * than the one this tab holds: another tab signed in as someone else. Starts the switch and
   * returns the user this tab keeps meanwhile. Returns `undefined` for the same account, or when
   * either side has no id to compare, and the caller carries on.
   *
   * Returns the promise itself rather than being async, so the common same-account path adds no
   * await before the caller's next request.
   */
  private switchIfOtherAccount(
    otherUserId: string | undefined
  ): Promise<AuthUser | null> | undefined {
    if (!this.currentUser?.id || !otherUserId || otherUserId === this.currentUser.id) {
      return undefined
    }
    return this.accountChangedElsewhere().then(() => this.currentUser)
  }

  /**
   * The signed-in account changed in another tab, so everything per-user here (Redux, account
   * status and company caches) is stale; a reload is the one reset that reaches all of it. A
   * player in a room keeps the session they are in and is reloaded when the room is left
   * (ROOM_LEFT, the signal the LiveKit connector uses too); leaving through the menu reloads the
   * page on its own. Anyone else, a player kicked back to the dashboard included, is reloaded at
   * once: their ROOM_LEFT is already past.
   */
  private async accountChangedElsewhere(): Promise<void> {
    if (this.accountSwitchPending) return
    this.accountSwitchPending = true

    let inRoom: boolean
    try {
      // Lazy: parseCloud imports this module. It owns the room test.
      const { isPlayerInRoomNow } = await import('./parseCloud')
      inRoom = await isPlayerInRoomNow()
    } catch (error) {
      // Not knowing is no reason to drop a player from a meeting; the next tab return asks again.
      console.warn('[AuthService] Could not tell whether the player is in a room:', error)
      this.accountSwitchPending = false
      return
    }
    if (!this.accountSwitchPending) return // signed out or in again while we were asking

    if (!inRoom) {
      this.reloadPage()
      return
    }

    const reload = () => {
      this.accountSwitchReload = null
      this.reloadPage()
    }
    this.accountSwitchReload = reload
    phaserEvents.once(GameEvent.ROOM_LEFT, reload)
  }

  private cancelAccountSwitchReload(): void {
    this.accountSwitchPending = false
    if (this.accountSwitchReload) {
      phaserEvents.off(GameEvent.ROOM_LEFT, this.accountSwitchReload)
      this.accountSwitchReload = null
    }
  }

  private reloadPage(): void {
    if (typeof window === 'undefined') return
    // The leave prompt belongs to the player's own navigation. Left on, it can answer this reload
    // with "Stay": the ROOM_LEFT that triggers it fires before React drops the prompt's listener.
    // Every other forced reload sets the flag too.
    ;(window as any).bypassBeforeUnload = true
    clearTimeout(this.reloadHeldBackTimer)
    this.reloadHeldBackTimer = setTimeout(() => {
      this.reloadHeldBackTimer = undefined
      // Still here, so the page did not go away. The player keeps their normal leave prompt, and
      // the next tab return finds the other account again instead of `checkSessionLight` serving
      // the old one until a manual refresh.
      ;(window as any).bypassBeforeUnload = false
      this.accountSwitchPending = false
    }, RELOAD_HELD_BACK_AFTER_MS)
    window.location.reload()
  }

  private async performSessionCheck(): Promise<AuthUser | null> {
    const epoch = this.sessionEpoch

    // First try to restore from localStorage
    let data: any = null
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      data = stored ? JSON.parse(stored) : null
    } catch {
      // Storage we cannot read holds no session to keep.
      await this.endSessionLocally()
      return null
    }

    const storedToken: string | undefined = data?.user?.sessionToken
    if (!storedToken) {
      // No valid session found in our custom storage
      // CRITICAL FIX: Ensure Parse SDK is also cleared to prevent desyncs where
      // Parse retains a stale session token that causes "Invalid session token" errors.
      await this.clearParseSession()
      return null
    }

    // This check also runs on a tab that holds a user but has nothing remembered (a refused
    // call while in a room, an inconclusive page-load check). `become` on the other tab's token
    // would turn the SDK, this service and the session context into that account while Redux
    // and the room still show this one. Start over as the new account instead.
    const switchingFromStored = this.switchIfOtherAccount(data?.user?.id)
    if (switchingFromStored) return switchingFromStored

    try {
      this.lastSessionVerifiedAt = Date.now()
      // Restore Parse session
      const user = await Parse.User.become(storedToken)
      if (epoch !== this.sessionEpoch) return this.currentUser

      // A stored copy without a user id could not be compared up front; Parse's answer can.
      const switchingFromParse = this.switchIfOtherAccount(user?.id)
      if (switchingFromParse) return switchingFromParse

      // Validate the session AND verify the company assignment. Both only need an active Parse
      // session (established by become()). A failed session check throws into the handler below.
      const { response, company, companyError } = await this.validateSessionAndCompany()
      if (epoch !== this.sessionEpoch) return this.currentUser

      this.currentUser = response.user
      this.currentProfile = response.profile

      // Sync Parse SDK token into wiggin_office_auth if it changed
      const liveToken = user.getSessionToken?.() ?? storedToken
      this.storeAuthData(response.user, response.profile, liveToken)

      // Log verification repairs if any (non-blocking)
      if (company) {
        noteCompanyCheck(company)
        if (company.repairs && company.repairs.length > 0) {
          console.log('[AuthService] Company assignment repairs applied after session check:', {
            userId: response.user.id,
            repairs: company.repairs.map((r) => r.type)
          })
        }
      } else {
        console.warn('[AuthService] Company assignment verification failed (non-blocking):', {
          error: companyError
        })
      }

      this.rememberSessionResult()
      return this.currentUser
    } catch (error) {
      return this.handleSessionCheckFailure(error, epoch, data, storedToken)
    }
  }

  /**
   * The cloud half of the page-load check: the session's user and profile, and the company check
   * (`company`, which only gets logged). One call, `checkSessionAndCompany`, reads the Profile
   * once and shares it with the company check, about half the requests of the two old calls.
   *
   * The cloud is uploaded by hand, possibly after this bundle ships. If it answers "Invalid
   * function", the old pair runs once as before (validateSession + verifyUserCompanyAssignment in
   * parallel, the company check never failing the session check), and the tab remembers to skip
   * the new call from then on. Every other failure, 209/206, a network error and any other 141
   * included, is thrown for `handleSessionCheckFailure` exactly as validateSession's was.
   *
   * `company` is null when the company check failed (inside the cloud call, or as the old pair's
   * second call); that is logged, never an error: the session is what decides.
   */
  private async validateSessionAndCompany(): Promise<{
    response: any
    company: CompanyCheckReply | null
    companyError?: string
  }> {
    if (!this.combinedCheckMissing) {
      try {
        const response = await Parse.Cloud.run('checkSessionAndCompany')
        const company = (response?.company ?? null) as CompanyCheckReply | null
        return {
          response,
          company,
          companyError: company ? undefined : 'the cloud could not run the check',
        }
      } catch (error) {
        if (!isMissingCloudFunction(error)) throw error
        console.warn(
          '[AuthService] checkSessionAndCompany is not in the cloud yet; using validateSession and verifyUserCompanyAssignment'
        )
        this.combinedCheckMissing = true
      }
    }

    const [sessionResult, verifyResult] = await Promise.allSettled([
      Parse.Cloud.run('validateSession'),
      Parse.Cloud.run('verifyUserCompanyAssignment', {}),
    ])
    if (sessionResult.status === 'rejected') throw sessionResult.reason

    return verifyResult.status === 'fulfilled'
      ? { response: sessionResult.value, company: verifyResult.value ?? null }
      : { response: sessionResult.value, company: null, companyError: verifyResult.reason?.message }
  }

  /**
   * Parse saying the session is invalid or missing (209/206) signs the user out. Anything else
   * (offline, a Back4App outage, a malformed reply) proves nothing about the session, so the user
   * stays signed in with the state we already hold and the result is not remembered: the next
   * check tries again.
   */
  private async handleSessionCheckFailure(
    error: unknown,
    epoch: number,
    stored: any,
    sentToken: string
  ): Promise<AuthUser | null> {
    // A sign-out or new sign-in replaced the session this check was about; it must not touch it.
    if (epoch !== this.sessionEpoch) return this.currentUser

    if (isInvalidSessionError(error)) {
      // Parse refused the token this check sent. Another tab (or adoptSessionToken) may have
      // stored a newer one since; clearing then would sign out a session that is fine.
      const newer = this.readStoredAuth()
      if (newer?.user?.sessionToken && newer.user.sessionToken !== sentToken) {
        return this.adoptNewerStoredSession(newer, sentToken, epoch)
      }
      await this.endSessionLocally()
      return null
    }

    console.warn('[AuthService] Session check inconclusive; keeping the user signed in', {
      code: (error as { code?: number } | null)?.code,
      message: (error as { message?: string } | null)?.message,
    })
    return this.restoreStoredUser(stored)
  }

  /**
   * Parse refused `refusedToken`, but another tab already stored a newer one (`fresh` is that
   * stored copy). Ask Parse about the newer token here. Setting only the in-memory token leaves the
   * SDK on the revoked one, and the next getCurrentUser() sees the two disagree and writes the
   * revoked token over the newer one in the storage every tab shares; the other tab's next check
   * would then send it, get 209 and sign out.
   *
   * Parse's answer decides, as everywhere else: 209/206 ends the session, a network error or a
   * timeout keeps the user (the outer 10 s race of each check covers a hung request).
   */
  private async adoptNewerStoredSession(
    fresh: any,
    refusedToken: string,
    epoch: number
  ): Promise<AuthUser | null> {
    const newerToken: string = fresh.user.sessionToken
    this.refusedSessionToken = refusedToken

    // The other tab did not just renew this session, it signed in as someone else.
    const switchingFromStored = this.switchIfOtherAccount(fresh.user.id)
    if (switchingFromStored) return switchingFromStored

    try {
      this.lastSessionVerifiedAt = Date.now()
      const user = await Parse.User.become(newerToken)
      if (epoch !== this.sessionEpoch) return this.currentUser

      // A stored copy without a user id could not be compared up front; Parse's answer can.
      const switchingFromParse = this.switchIfOtherAccount(user?.id)
      if (switchingFromParse) return switchingFromParse

      // On a page load nothing is held yet: the stored copy, written by the tab that holds the
      // newer token after its own full check, is the user's state. Without this the page-load
      // gate would cache "signed out" while the next check signs in.
      this.restoreStoredUser(fresh)
      if (this.currentUser) {
        this.currentUser.sessionToken = user?.getSessionToken?.() ?? newerToken
        this.rememberSessionResult()
      }
      return this.currentUser
    } catch (error) {
      if (epoch !== this.sessionEpoch) return this.currentUser

      if (isInvalidSessionError(error)) {
        await this.endSessionLocally()
        return null
      }

      console.warn('[AuthService] Newer session token not confirmed; keeping the user signed in', {
        code: (error as { code?: number } | null)?.code,
        message: (error as { message?: string } | null)?.message,
      })
      return this.restoreStoredUser(fresh)
    }
  }

  private readStoredAuth(): any {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  }

  /**
   * The session token in the storage every tab shares, if any. parseCloud compares it with the
   * token a refused call sent: another tab may have replaced the session since.
   */
  readStoredToken(): string | undefined {
    return this.readStoredAuth()?.user?.sessionToken || undefined
  }

  /**
   * The page-load check failed before it learned anything, so memory is still empty; the stored
   * copy of the last good check is the user's state.
   */
  private restoreStoredUser(stored: any): AuthUser | null {
    if (!this.currentUser && stored?.user?.id) {
      const createdAt = new Date(stored.user.createdAt ?? Date.now())
      this.currentUser = {
        ...stored.user,
        // JSON turned the Date into a string; callers call toISOString() on it.
        createdAt: Number.isNaN(createdAt.getTime()) ? new Date() : createdAt,
      }
      this.currentProfile = stored.profile ?? null
    }
    return this.currentUser
  }

  /** Drop the session this tab holds and tell the app, in that order, before any network wait. */
  private async endSessionLocally(): Promise<void> {
    // clearParseSession wipes Parse's local state before its first await and only then calls
    // logOut over the network; clear ours in the same turn so a sign-in that starts while the
    // logOut is out cannot have its fresh storage wiped.
    const parseCleared = this.clearParseSession()
    this.clearStoredAuth()
    this.currentUser = null
    this.currentProfile = null
    this.forgetSessionResult()
    this.notifySessionExpired()
    await parseCleared
  }

  private notifySessionExpired(): void {
    if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return
    window.dispatchEvent(
      new CustomEvent('sessionExpired', { detail: { message: SESSION_EXPIRED_MESSAGE } })
    )
  }

  /**
   * Debug method to validate user and profile data
   */
  async debugUserProfile(userId: string): Promise<any> {
    try {
      console.log('[AuthService] Debugging user profile for:', userId)

      const response = await Parse.Cloud.run('debugUserProfile', {
        userId: userId
      })

      console.log('[AuthService] Debug response:', response)
      return response
    } catch (error: any) {
      console.error('[AuthService] Debug error:', error)
      return { error: error.message }
    }
  }

  /**
   * Get current profile data, ensuring it's always loaded
   * Enhanced to handle Google OAuth users and provide better error handling
   */
  async getCurrentProfile(): Promise<UserProfile | null> {
    if (!this.currentUser) {
      console.warn('[AuthService] No current user found for profile loading')
      return null
    }

    // If profile is already loaded, return it
    if (this.currentProfile) {
      console.log('[AuthService] Returning cached profile for user:', this.currentUser.id)
      return this.currentProfile
    }

    try {
      console.log('[AuthService] Loading profile for user:', this.currentUser.id)

      // Try cloud function first
      let profile: any = null
      let useFallback = false

      try {
        profile = await Parse.Cloud.run('getUserProfile', {
          userId: this.currentUser.id
        })
      } catch (error: any) {
        const errorType = this.detectErrorType(error)
        console.warn('[AuthService] Cloud function profile load failed, using fallback', {
          errorType,
          code: error.code,
          userId: this.currentUser.id
        })

        // Use fallback for 404 or network errors
        if (errorType === 'NOT_FOUND' || errorType === 'NETWORK') {
          useFallback = true
        } else {
          throw error
        }
      }

      // Use fallback if cloud function failed
      if (useFallback || !profile) {
        const context = {
          userId: this.currentUser.id,
          timestamp: new Date().toISOString(),
          operation: 'getCurrentProfile'
        }
        profile = await this.loadProfileWithFallback(this.currentUser.id, context)
      }

      if (profile) {
        // getUserProfile returns only part of the profile (no profileCompleted, gender or date of
        // birth), so always fill the defaults rather than passing the response through.
        const mappedProfile: UserProfile = {
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          displayName: profile.displayName || '',
          title: profile.title || '',
          bio: profile.bio || '',
          contact: profile.contact || '',
          avatar: profile.avatar || 'beam',
          customAvatarUrl: profile.customAvatarUrl || '',
          gameCharacter: profile.gameCharacter || 'adam',
          role: profile.role || 'user',
          profileCompleted: profile.profileCompleted ?? false,
          gender: profile.gender || '',
          dateOfBirth: profile.dateOfBirth || undefined,
          avatarSource: profile.avatarSource,
          avatarUpdatedAt: profile.avatarUpdatedAt,
        }

        console.log('[AuthService] Profile loaded successfully:', {
          userId: this.currentUser.id,
          firstName: mappedProfile.firstName,
          lastName: mappedProfile.lastName,
          role: mappedProfile.role,
          profileCompleted: mappedProfile.profileCompleted
        })

        this.currentProfile = mappedProfile

        // Update stored data to ensure profile is cached
        this.storeAuthData(this.currentUser, this.currentProfile, this.currentUser?.sessionToken)

        return this.currentProfile
      } else {
        console.warn('[AuthService] No profile found for user:', this.currentUser.id)

        // Try to create a default profile if none exists (for Google OAuth users)
        try {
          console.log('[AuthService] Attempting to create default profile for user:', this.currentUser.id)

          try {
            const defaultProfile = await Parse.Cloud.run('createDefaultProfile', {
              userId: this.currentUser.id,
              email: this.currentUser.email,
              username: this.currentUser.username
            })

            if (defaultProfile) {
              console.log('[AuthService] Default profile created successfully')
              this.currentProfile = defaultProfile
              this.storeAuthData(this.currentUser, this.currentProfile, this.currentUser?.sessionToken)
              return this.currentProfile
            }
          } catch (createError: any) {
            const errorType = this.detectErrorType(createError)
            if (errorType === 'NOT_FOUND' || errorType === 'NETWORK') {
              console.warn('[AuthService] Cannot create default profile - cloud function unavailable')
            } else {
              console.error('[AuthService] Failed to create default profile:', createError)
            }
          }
        } catch (createError) {
          console.error('[AuthService] Failed to create default profile:', createError)
        }

        return null
      }

    } catch (error) {
      console.error('[AuthService] Error loading profile for user:', this.currentUser.id, error)
      return null
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(userId: string, updates: Partial<UserProfile>): Promise<{ data: UserProfile | null; error: string | null }> {
    try {

      // Check for current user session
      let currentUser = Parse.User.current()

      // If no current user but we have stored user data, this might be a new signup
      if (!currentUser && this.currentUser && this.currentUser.id === userId) {

        // Try to establish session first if we have a session token in localStorage
        const stored = localStorage.getItem(this.STORAGE_KEY)
        if (stored) {
          const data = JSON.parse(stored)
          if (data.user?.sessionToken) {
            try {
              currentUser = await Parse.User.become(data.user.sessionToken)
            } catch (sessionError) {
              // Ignore session errors
            }
          }
        }

        // Also try to establish session from currentUser if it has a session token
        if (!currentUser && this.currentUser?.sessionToken) {
          try {
            currentUser = await Parse.User.become(this.currentUser.sessionToken)
          } catch (sessionError) {
            // Ignore session errors
          }
        }

        // If we still don't have a session, try the cloud function approach for new users
        if (!currentUser) {

          try {
            const response = await Parse.Cloud.run('updateNewUserProfile', {
              userId: userId,
              updates: updates
            })

            if (response && response.profile) {
              this.currentProfile = response.profile
              this.storeAuthData(this.currentUser, this.currentProfile, this.currentUser?.sessionToken)
              return { data: response.profile, error: null }
            }
          } catch (cloudError) {
            throw new Error('Unable to update profile for new user. Please try refreshing the page.')
          }
        }
      }

      if (!currentUser) {
        throw new Error('User not authenticated. Please log in again.')
      }

      // Normal flow for authenticated users
      const Profile = Parse.Object.extend('Profile')
      const query = new Parse.Query(Profile)
      query.equalTo('userId', userId)

      let profile
      try {
        profile = await query.first()
      } catch (queryError) {
        console.warn('[AuthService] Query failed:', queryError)
        profile = null
      }

      if (!profile) {
        profile = new Profile()
        profile.set('userId', userId)
        profile.set('username', currentUser.get('username'))
        profile.set('email', currentUser.get('email'))
        profile.set('role', 'user')
        profile.set('avatar', 'adam')
        profile.set('gameCharacter', 'adam')
        profile.set('firstName', '')
        profile.set('lastName', '')
        profile.set('title', '')
        profile.set('bio', '')
        profile.set('contact', '')
        profile.set('customAvatarUrl', '')
        profile.set('profileCompleted', false)
        profile.set('gender', '')
        profile.set('dateOfBirth', null)

        const acl = new Parse.ACL(currentUser)
        acl.setRoleReadAccess('admin', true)
        profile.setACL(acl)
      }

      // Update fields - ensure gender and dateOfBirth are always set (even if empty)
      Object.keys(updates).forEach(key => {
        if (updates[key as keyof UserProfile] !== undefined) {
          profile.set(key, updates[key as keyof UserProfile])
        }
      })

      // Explicitly ensure gender and dateOfBirth are saved (even if empty/null)
      if ('gender' in updates) {
        profile.set('gender', updates.gender || '')
      }
      if ('dateOfBirth' in updates) {
        profile.set('dateOfBirth', updates.dateOfBirth || null)
      }

      const avatarTouched =
        updates.customAvatarUrl !== undefined || updates.avatar !== undefined
      if (avatarTouched) {
        const rawUrl = (profile.get('customAvatarUrl') as string) || ''
        if (rawUrl.trim()) {
          profile.set('customAvatarUrl', withAvatarCacheBust(rawUrl, Date.now()))
        }
        const derived: UserProfile = {
          firstName: profile.get('firstName') || '',
          lastName: profile.get('lastName') || '',
          avatar: profile.get('avatar') || 'adam',
          customAvatarUrl: profile.get('customAvatarUrl') || '',
        }
        profile.set('avatarSource', resolveAvatarSource(derived))
        profile.set('avatarUpdatedAt', new Date())
      }

      await profile.save()

      // Convert to UserProfile format
      const updatedProfile: UserProfile = {
        firstName: profile.get('firstName') || '',
        lastName: profile.get('lastName') || '',
        displayName: profile.get('displayName') || '',
        title: profile.get('title') || '',
        contact: profile.get('contact') || '',
        bio: profile.get('bio') || '',
        avatar: profile.get('avatar') || 'adam',
        gameCharacter: profile.get('gameCharacter') || 'adam',
        customAvatarUrl: profile.get('customAvatarUrl') || '',
        role: profile.get('role') || 'user',
        profileCompleted: profile.get('profileCompleted') || false,
        gender: profile.get('gender') || '',
        dateOfBirth: profile.get('dateOfBirth') || undefined,
        avatarSource: profile.get('avatarSource') || undefined,
        avatarUpdatedAt: profile.get('avatarUpdatedAt') || undefined,
      }

      this.currentProfile = updatedProfile
      this.storeAuthData(this.currentUser, this.currentProfile, this.currentUser?.sessionToken)

      // Broadcast profile update to all systems
      this.broadcastProfileUpdate(updatedProfile)

      return { data: updatedProfile, error: null }

    } catch (error: any) {
      return { data: null, error: error.message || 'Failed to update profile' }
    }
  }

  /**
   * Broadcast profile updates to all relevant systems
   */
  private broadcastProfileUpdate(profile: UserProfile): void {
    try {
      notifyProfileObservers({ userId: this.currentUser?.id, profile })
    } catch (error) {
      // Silently handle broadcast errors
    }
  }

  /**
   * Clear profile-related caches to ensure fresh data
   */
  private clearProfileCaches(): void {
    try {
      // Clear any notification or user profile caches
      // This ensures that systems using cached profile data will refresh

      // Dispatch cache clear event for systems that need to know
      const cachesClearEvent = new CustomEvent('profileCachesCleared', {
        detail: { userId: this.currentUser?.id }
      })
      window.dispatchEvent(cachesClearEvent)

    } catch (error) {
      // Silently handle cache clear errors
    }
  }

  /**
   * Get Google OAuth URL for client-side redirect
   * Returns URL, clientId, and state for CSRF protection
   */
  async getGoogleOAuthURL(redirectUri: string): Promise<{ url: string; clientId: string; state?: string }> {
    try {
      console.log('[AuthService] Getting Google OAuth URL for redirect URI:', redirectUri)

      const response = await Parse.Cloud.run('getGoogleOAuthURL', {
        redirectUri
      })

      console.log('[AuthService] Received OAuth URL with state:', response.state ? response.state.substring(0, 10) + '...' : 'none')

      return response
    } catch (error: any) {
      console.error('[AuthService] Error getting Google OAuth URL:', error)
      throw this.formatError(error)
    }
  }

  /**
   * The sign-in / sign-up step of the Google callback in App.tsx (SC-360). It forwards the
   * Create Account marketing choice stored when the flow started, so a Google sign-up records
   * it (AC3); without one, cloud records nothing and the Terms screen asks instead. Kept here
   * rather than inline in App.tsx so the forwarding is unit-tested. `state` is the callback's
   * validated OAuth state: only the choice stored by that same flow is sent, never one another
   * tab left in the shared localStorage.
   */
  async handleGoogleOAuthCallbackWithStoredConsent(
    code: string,
    redirectUri: string,
    state: string | null
  ): Promise<AuthResponse> {
    return this.handleGoogleOAuthCallback(code, redirectUri, readGoogleOAuthSignupConsent(state))
  }

  /**
   * Handle Google OAuth callback and authenticate user
   * Enhanced to ensure profile data is properly loaded and cached
   *
   * `signupConsent` is sent only when the flow started on Create Account (SC-360). Cloud
   * applies it to a newly created account only, never to an existing one.
   */
  async handleGoogleOAuthCallback(
    code: string,
    redirectUri: string,
    signupConsent?: SignupConsent | null
  ): Promise<AuthResponse> {
    try {
      console.log('[AuthService] Starting Google OAuth callback handling')

      this.invalidateSessionChecks()
      // Clear any existing session
      try {
        await this.clearParseSession()
        this.currentUser = null
        this.currentProfile = null
      } catch (logoutError) {
        // Ignore logout errors
      }

      // Use cloud function to handle OAuth callback
      const response = await Parse.Cloud.run('handleGoogleOAuthCallback', {
        code,
        redirectUri,
        ...(signupConsent ? { signupConsent } : {})
      })

      console.log('[AuthService] Google OAuth callback response received:', {
        userId: response.user?.id,
        hasProfile: !!response.profile,
        profileRole: response.profile?.role,
        hasSessionToken: !!response.user?.sessionToken
      })

      // CRITICAL FIX: Validate user response
      if (!response.user || !response.user.id) {
        console.error('[AuthService] Invalid user response from OAuth callback:', response)
        throw new Error('Invalid user response from OAuth callback - user ID is missing')
      }

      // CRITICAL FIX: Validate session token is present
      if (!response.user?.sessionToken) {
        console.error('[AuthService] No session token in OAuth response:', response)
        throw new Error('No session token received from OAuth callback - authentication failed')
      }

      // Establish Parse session immediately and verify it worked
      try {
        const establishedUser = await Parse.User.become(response.user.sessionToken)
        console.log('[AuthService] Parse session established with token:', {
          userId: establishedUser.id,
          sessionToken: establishedUser.getSessionToken()?.substring(0, 20) + '...',
          currentUserMatches: Parse.User.current()?.id === establishedUser.id
        })

        // CRITICAL FIX: Verify session is actually established
        const currentUser = Parse.User.current()
        if (!currentUser || currentUser.id !== establishedUser.id) {
          console.error('[AuthService] Session establishment failed - current user mismatch')
          throw new Error('Failed to establish user session after OAuth callback')
        }

        // Verify session token is accessible
        const sessionToken = currentUser.getSessionToken()
        if (!sessionToken || sessionToken !== response.user.sessionToken) {
          console.warn('[AuthService] Session token mismatch, using established token')
          // Use the token from the established user
          response.user.sessionToken = sessionToken || response.user.sessionToken
        }
      } catch (sessionError: any) {
        console.error('[AuthService] Failed to establish Parse session:', sessionError)
        throw new Error(`Failed to establish user session: ${sessionError.message || 'Unknown error'}`)
      }

      const authResponse: AuthResponse = {
        user: {
          ...response.user,
          sessionToken: response.user.sessionToken,
        },
        profile: response.profile,
        isNewUser: Boolean((response as { isNewUser?: boolean }).isNewUser),
      }

      // Store in memory and localStorage - CRITICAL: Do this AFTER session is established
      this.currentUser = authResponse.user
      this.currentProfile = authResponse.profile
      this.storeAuthData(authResponse.user, authResponse.profile, authResponse.user.sessionToken)
      const oauthCompany = (response as { company?: { id?: string; name?: string } }).company
      const oauthTrack: TrackAuthSuccessOptions = {
        authFlow: 'oauth_google',
        authMethod: 'google',
        email: authResponse.user.email,
        username: authResponse.user.username,
      }
      if (oauthCompany?.id) {
        oauthTrack.companyAssigned = true
        oauthTrack.companyId = oauthCompany.id
        if (oauthCompany.name) oauthTrack.companyName = oauthCompany.name
      }
      trackAuthSuccess(oauthTrack)

      // CRITICAL FIX: Ensure profile data is loaded even if not returned by OAuth callback
      if (!authResponse.profile) {
        console.log('[AuthService] No profile in OAuth response, loading profile data...')
        try {
          const profile = await this.getCurrentProfile()
          if (profile) {
            console.log('[AuthService] Profile loaded after OAuth:', {
              firstName: profile.firstName,
              lastName: profile.lastName,
              role: profile.role,
              profileCompleted: profile.profileCompleted
            })
            authResponse.profile = profile
            this.currentProfile = profile
            this.storeAuthData(authResponse.user, authResponse.profile, authResponse.user.sessionToken)
          }
        } catch (profileError) {
          console.error('[AuthService] Error loading profile after OAuth:', profileError)
        }
      }

      // Check if user was assigned to a company and requires billing
      if (response.company && response.company.requiresBilling) {
        console.log('[AuthService] User assigned to company via OAuth, processing billing:', response.company.name)
        try {
          await this.processCompanyBilling(response.company.id, authResponse.user.id, authResponse.user.email)
        } catch (billingError) {
          console.error('[AuthService] OAuth billing processing failed:', billingError)
          // Don't fail the OAuth if billing fails
        }
      }

      void this.notifySecurityDeviceCheck(authResponse.user.sessionToken)

      this.resolveSessionReady(this.currentUser)
      console.log('[AuthService] Google OAuth authentication completed successfully')
      return authResponse

    } catch (error: any) {
      console.error('[AuthService] Google OAuth callback error:', error)
      // Clear any partial state on error
      this.currentUser = null
      this.currentProfile = null
      this.clearStoredAuth()
      throw this.formatError(error)
    }
  }

  /**
   * Validate Google OAuth token (for client-side validation)
   */
  async validateGoogleToken(idToken: string): Promise<{ valid: boolean; email?: string; emailVerified?: boolean; name?: string; picture?: string; error?: string }> {
    try {

      const response = await Parse.Cloud.run('validateGoogleToken', {
        idToken
      })

      return response
    } catch (error: any) {
      return {
        valid: false,
        error: error.message
      }
    }
  }

  /**
   * Sign out user and clear all data
   */
  async signOut(): Promise<void> {
    clearPendingCompanyInvite()
    try {
      // Leaving the room below emits ROOM_LEFT; a reload now would cut the sign-out short.
      this.cancelAccountSwitchReload()
      // Leave Colyseus room first while the socket is still open so peers get PLAYER_LEFT immediately.
      const { disconnectRoomBeforeSignOut } = await import('./disconnectRoomBeforeSignOut')
      disconnectRoomBeforeSignOut()

      let enhancedSignOutSucceeded = false

      // Use cloud function for enhanced sign out (destroys session on server)
      if (this.currentUser) {
        try {
          await Parse.Cloud.run('enhancedSignOut')
          enhancedSignOutSucceeded = true
        } catch (error) {
          // Ignore cloud sign out errors
        }
      }

      // Always use the robust session clearing method to ensure local caches 
      // and storage are properly cleaned up, avoiding "Invalid session token" errors
      // on subsequent logins without a page refresh.
      await this.clearParseSession()

      // Clear all data
      this.currentUser = null
      this.currentProfile = null
      this.clearStoredAuth()
      // The Matrix session is stored under one browser-wide key; leaving it behind
      // hands the next account to sign in on this device our chat identity (SC-340).
      clearChatSession()
      this.resetSessionReadyGate()
      clearWorkspaceLandingHandled()
      clearWorkspaceEnteredSession()
      accountStatusService.clearCache()
      CompanyService.clearCache()
      clearCompanyAccessState()
      clearRbacState()
      usePermissionStore.getState().resetAdminSessionCapabilities()
      // The cached background blobs are the account's pictures, and signing out does not
      // reload the page: without this the next account reuses them by id.
      releaseAllBackgroundImageUrls()
    } catch (error) {
      // Clear data even if cloud function fails
      this.currentUser = null
      this.currentProfile = null
      this.clearStoredAuth()
      // The Matrix session is stored under one browser-wide key; leaving it behind
      // hands the next account to sign in on this device our chat identity (SC-340).
      clearChatSession()
      this.resetSessionReadyGate()
      clearWorkspaceLandingHandled()
      clearWorkspaceEnteredSession()
      accountStatusService.clearCache()
      CompanyService.clearCache()
      clearCompanyAccessState()
      clearRbacState()
      usePermissionStore.getState().resetAdminSessionCapabilities()
      // The cached background blobs are the account's pictures, and signing out does not
      // reload the page: without this the next account reuses them by id.
      releaseAllBackgroundImageUrls()
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.checkSession()
    return user !== null
  }

  /**
   * Validate that the current session is using proper user authentication
   * and not master key authentication
   * Enhanced to handle OAuth sessions correctly
   */
  private validateSessionAuthentication(): boolean {
    try {
      const currentUser = Parse.User.current()
      if (!currentUser) {
        // If we have a stored user but no Parse session, try to restore it
        if (this.currentUser?.sessionToken) {
          console.log('[AuthService] Attempting to restore Parse session from stored token')
          // Don't restore here - let checkSession() handle it
          return false
        }
        return false
      }

      const sessionToken = currentUser.getSessionToken()
      if (!sessionToken) {
        console.warn('[AuthService] Parse.User.current() exists but has no session token')
        // If we have a stored session token, the session might still be valid
        // This can happen right after OAuth callback before Parse fully syncs
        if (this.currentUser?.sessionToken) {
          console.log('[AuthService] Using stored session token for validation')
          return true
        }
        return false
      }

      // Verify the session token matches our stored token (if we have one)
      if (this.currentUser?.sessionToken && this.currentUser.sessionToken !== sessionToken) {
        console.warn('[AuthService] Session token mismatch between Parse and stored user')
        // Update stored token to match Parse
        this.currentUser.sessionToken = sessionToken
        this.storeAuthData(this.currentUser, this.currentProfile, sessionToken)
      }

      // Check if we're using master key (which should never happen on client-side)
      // This check is less reliable but we'll keep it
      try {
        const headers = Parse._getInstallationId ? Parse._getInstallationId() : null
        if (headers && headers['X-Parse-Master-Key']) {
          console.warn('[AuthService] Master key detected in headers - invalidating session')
          return false
        }
      } catch (headerError) {
        // Ignore header check errors
      }

      return true
    } catch (error) {
      console.error('[AuthService] Error validating session authentication:', error)
      return false
    }
  }

  /**
   * Get current user
   * Enhanced to handle OAuth sessions and provide better error recovery
   */
  getCurrentUser(): AuthUser | null {
    // If we have a stored user but no Parse session, try to restore it
    if (this.currentUser && !Parse.User.current()) {
      // Only log in debug mode - this is expected during session restoration
      if (process.env.NODE_ENV === 'development') {
        console.log('[AuthService] Stored user exists but no Parse session - session may need restoration')
      }
      // Don't return null immediately - the session might be restored by checkSession()
      // But we should still validate what we have
    }

    // Validate session authentication before returning user
    const isValid = this.validateSessionAuthentication()

    if (!isValid) {
      // If validation failed but we have a stored user with session token, 
      // it might be a timing issue - log it but don't clear the user yet
      if (this.currentUser?.sessionToken) {
        // This is a potential issue - user exists but session is invalid
        console.warn('[AuthService] Session authentication validation failed but stored user exists - may need session restoration')
        // Return null to trigger session check, but don't clear stored data yet
        return null
      }

      // No stored user and no Parse session - this is normal when user is not logged in
      // Only log in development mode to reduce noise in production
      if (process.env.NODE_ENV === 'development') {
        console.log('[AuthService] No user session (user not logged in)')
      }
      return null
    }

    return this.currentUser
  }

  /**
   * Store authentication data in localStorage
   */
  private storeAuthData(user: AuthUser, profile: UserProfile | null, sessionToken?: string): void {
    try {
      // Saving the in-memory user (profile loads and updates) while the SDK or memory still holds
      // a token Parse refused must keep the newer token another tab stored.
      const token =
        sessionToken && sessionToken === this.refusedSessionToken
          ? this.readStoredToken() ?? sessionToken
          : sessionToken
      const userWithSession = token ? { ...user, sessionToken: token } : user
      const data = {
        user: userWithSession,
        profile,
        timestamp: Date.now()
      }
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      // Silently handle storage errors
    }
  }

  /**
   * Clear Parse SDK session and its localStorage cache so stale tokens are not sent.
   */
  private async clearParseSession(): Promise<void> {
    // RACE CONDITION FIX: ALL synchronous cleanup (memory + localStorage) must happen
    // before any `await`. After _clearCache() sets currentUserCacheMatchesDisk=false
    // but before localStorage is cleared, React can yield control, re-render the auth
    // dialog, and Parse will re-read the stale token from localStorage — causing
    // "Invalid session token" errors on public cloud function calls.

    // Step 1: Wipe Parse SDK in-memory cache synchronously.
    try {
      if (typeof (Parse.User as any)._clearCache === 'function') {
        ;(Parse.User as any)._clearCache()
      }
    } catch { /* ignore */ }
    try {
      if ((Parse.User as any)._currentUser !== undefined) {
        ;(Parse.User as any)._currentUser = null
      }
    } catch { /* ignore */ }

    // Step 2: Synchronously remove ALL Parse session tokens from localStorage BEFORE
    // yielding (no awaits yet). This prevents Parse from re-loading the ghost token
    // during any React re-render that may occur during the async signOut network call.
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('Parse/') && key.endsWith('/currentUser')) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k))
    } catch { /* ignore */ }

    // Step 3: NOW we can await — local state is already fully clean.
    // Fire the network logOut to invalidate the server-side session.
    try {
      await Parse.User.logOut()
    } catch {
      // Expected when server-side session is already expired/invalid.
    }
  }

  /**
   * Clear stored authentication data
   */
  private clearStoredAuth(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
    } catch (error) {
      // Silently handle clear errors
    }
  }

  /**
   * Process company billing when user is assigned to a company
   */
  private async processCompanyBilling(companyId: string, userId: string, userEmail: string): Promise<void> {
    try {
      console.log('[AuthService] Calling server billing endpoint for company:', companyId)

      const response = await fetch('/api/billing/auto-bill-new-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId,
          userId,
          userEmail,
          isAutoAssigned: true
        })
      })

      const text = await response.text()

      if (!response.ok) {
        let errorMessage = 'Unknown error'
        if (text && text.trim()) {
          try {
            const errorData = JSON.parse(text)
            errorMessage = errorData.error || errorMessage
          } catch {
            errorMessage = text.substring(0, 200)
          }
        }
        throw new Error(`Billing failed: ${errorMessage}`)
      }

      let result: Record<string, unknown> = {}
      if (text && text.trim()) {
        try {
          result = JSON.parse(text)
        } catch {
          // Empty or non-JSON success response - treat as success
          console.log('[AuthService] Billing response received (non-JSON body), treating as success')
        }
      }
      if (Object.keys(result).length > 0) {
        console.log('[AuthService] Billing processed successfully:', result)
      }

    } catch (error) {
      console.error('[AuthService] Error processing company billing:', error)
      throw error
    }
  }

  /**
   * Adopt a session token minted outside the normal sign-in paths.
   *
   * Saving a password revokes every existing session for that user, so
   * `setAccountPassword` hands back a replacement. Calling `Parse.User.become` on it is
   * not enough by itself: this service restores the token from its own `STORAGE_KEY`
   * copy on boot, so leaving the revoked one there signs the user out on their next
   * reload. That is what stranded a fresh invitee on the profile step — the tab looked
   * fine, every cloud call silently failed, and a refresh dropped them at sign-in.
   * Persist the replacement alongside becoming it. [SC-313]
   */
  async adoptSessionToken(sessionToken: string): Promise<void> {
    const user = await Parse.User.become(sessionToken)
    const liveToken = user.getSessionToken() || sessionToken
    const base = this.currentUser ?? {
      id: String(user.id),
      username: user.getUsername() ?? '',
      email: user.getEmail() ?? '',
      createdAt: user.createdAt ? new Date(user.createdAt) : new Date(),
    }
    this.currentUser = { ...base, sessionToken: liveToken }
    this.storeAuthData(this.currentUser, this.currentProfile, liveToken)

    // A check still waiting on the revoked token must not sign out the replacement. `become`
    // just verified the session, so a result we already held stays valid.
    const wasRemembered = this.sessionResultRemembered
    this.invalidateSessionChecks()
    if (wasRemembered) this.rememberSessionResult()
  }

  /**
   * After redeeming a company invitation Cloud function, establish Parse session and persist auth (localStorage).
   */
  async establishSessionFromInvitationMagicLink(sessionToken: string): Promise<AuthResponse> {
    // This is an identity switch without a logout: the invitee's link is usually opened in the
    // same browser the inviting admin is signed into. Anything cached against the previous user
    // — company, admin flag — has to go, or the invitee lands in the admin's company. The cache
    // entries are owner-stamped as a backstop, but clear here too so nothing reads a stale
    // value in the window before `become()` settles.
    accountStatusService.clearCache()
    CompanyService.clearCache()
    this.invalidateSessionChecks()

    await Parse.User.become(sessionToken)
    const response = (await Parse.Cloud.run('validateSession')) as {
      user: {
        id: string
        username: string
        email: string
        sessionToken: string
        createdAt?: string | Date
      }
      profile: {
        firstName?: string
        lastName?: string
        title?: string
        bio?: string
        contact?: string
        avatar?: string
        gameCharacter?: string
        customAvatarUrl?: string
        profileCompleted?: boolean
        role?: string
        displayName?: string
      } | null
    }

    const createdAt = response.user.createdAt
    const authUser: AuthUser = {
      id: response.user.id,
      username: response.user.username,
      email: response.user.email,
      createdAt:
        createdAt == null
          ? new Date()
          : typeof createdAt === 'string'
            ? new Date(createdAt)
            : new Date(createdAt),
      sessionToken: response.user.sessionToken,
    }

    const profile: UserProfile | null = response.profile
      ? {
          firstName: response.profile.firstName || '',
          lastName: response.profile.lastName || '',
          displayName: response.profile.displayName || '',
          title: response.profile.title || '',
          bio: response.profile.bio || '',
          contact: response.profile.contact || '',
          avatar: response.profile.avatar || 'adam',
          gameCharacter: response.profile.gameCharacter || 'adam',
          customAvatarUrl: response.profile.customAvatarUrl || '',
          profileCompleted: response.profile.profileCompleted || false,
          role: response.profile.role || 'user',
        }
      : null

    this.currentUser = { ...authUser, sessionToken }
    this.currentProfile = profile
    this.storeAuthData(this.currentUser, this.currentProfile, sessionToken)

    let companyAssigned: boolean | undefined
    let companyId: string | null | undefined
    let companyName: string | null | undefined
    try {
      const verificationResult = (await Parse.Cloud.run('verifyUserCompanyAssignment', {})) as {
        repairs?: { type?: string }[]
        finalState?: { companyId?: string | null }
      }
      noteCompanyCheck(verificationResult)
      const cid = verificationResult?.finalState?.companyId
      if (cid) {
        companyAssigned = true
        companyId = String(cid)
        companyName = null
      }
      if (verificationResult.repairs && verificationResult.repairs.length > 0) {
        console.log('[AuthService] Company assignment repairs after invitation session:', {
          userId: authUser.id,
          repairs: verificationResult.repairs.map((r: any) => r.type),
        })
      }
    } catch (e: any) {
      console.warn('[AuthService] verifyUserCompanyAssignment failed after invitation (non-blocking):', e?.message)
    }

    trackAuthSuccess({
      authFlow: 'invitation_magic_link',
      authMethod: 'invitation_magic_link',
      email: authUser.email,
      username: authUser.username,
      ...(companyAssigned === true ? { companyAssigned: true as const } : {}),
      ...(companyId ? { companyId } : {}),
      ...(companyName ? { companyName } : {}),
    })

    // become + validateSession + verifyUserCompanyAssignment is a full check.
    this.rememberSessionResult()

    return { user: this.currentUser, profile: profile || undefined }
  }

  /**
   * Format error for consistent error handling
   */
  private formatError(error: any, errorType?: 'NOT_FOUND' | 'NETWORK' | 'AUTHENTICATION' | 'VALIDATION' | 'OTHER'): AuthError {
    // Detect error type if not provided
    if (!errorType) {
      errorType = this.detectErrorType(error)
    }

    const googleAuthMessage =
      typeof error?.message === 'string' &&
      (error.message.includes('Google Sign-In') || error.message.includes('registered with Google'))

    // Handle Parse errors
    if (error instanceof Parse.Error) {
      let message = error.message

      if (googleAuthMessage) {
        return {
          code: error.code,
          message: error.message,
          field: error.message.includes('registered with Google') ? 'google' : 'google',
        }
      }

      // Enhance error messages based on error type
      if (errorType === 'NOT_FOUND') {
        message = 'Authentication service temporarily unavailable. Please try again.'
      } else if (errorType === 'NETWORK') {
        message = 'Network error. Please check your connection and try again.'
      } else if (errorType === 'AUTHENTICATION') {
        // Keep original message for authentication errors
        message = error.message || 'Invalid username/password.'
      } else if (errorType === 'VALIDATION') {
        message = error.message || 'Please check your input and try again.'
      }

      return {
        code: error.code,
        message: message,
        field: this.getErrorField(error.code, errorType)
      }
    }

    // Handle generic errors
    let message = error.message || 'An unexpected error occurred'

    if (errorType === 'NOT_FOUND') {
      message = 'Authentication service temporarily unavailable. Please try again.'
    } else if (errorType === 'NETWORK') {
      message = 'Network error. Please check your connection and try again.'
    }

    return {
      code: error.code || 999,
      message: message,
      field: 'general'
    }
  }

  /** Drop cached profile so the next `getCurrentProfile()` fetches from the server. */
  clearInMemoryProfileCache(): void {
    this.currentProfile = null
  }

  /** Link Google to the current Parse session (OAuth code from same redirect URI as login). */
  async linkGoogleAccountWithCode(code: string, redirectUri: string): Promise<{
    success: boolean
    picture?: string | null
    message?: string
  }> {
    return Parse.Cloud.run('linkGoogleAccountToCurrentUser', { code, redirectUri })
  }

  /** Remove Google link from the current user (keeps password / email sign-in). */
  async unlinkGoogleAccountFromCurrentUser(): Promise<{ success: boolean; message?: string }> {
    return Parse.Cloud.run('unlinkGoogleAccountFromCurrentUser', {})
  }

  /**
   * Set or change password for the signed-in user (optional for Google-only accounts).
   *
   * Returns a replacement `sessionToken`, because changing the password revokes the
   * session that made the call. Callers must adopt it before touching Parse again.
   * Absent when the cloud function predates that change, or when re-authentication
   * failed server-side — the password is saved either way. [SC-313]
   *
   * `consent` is sent only by the invite password step, which records Terms and the
   * marketing choice with the save (SC-360). Profile never sends it.
   */
  async setAccountPassword(
    newPassword: string,
    currentPassword?: string,
    consent?: SignupConsent,
  ): Promise<{ success: boolean; message?: string; sessionToken?: string | null }> {
    try {
      return await Parse.Cloud.run('setAccountPassword', {
        newPassword,
        ...(currentPassword ? { currentPassword } : {}),
        ...(consent ? { consent } : {}),
      })
    } catch (error: any) {
      throw this.formatError(error)
    }
  }

  /**
   * Request a password-reset email for an address.
   *
   * Hits the server endpoint rather than a cloud function (password reset is
   * server-side). Used by admin tooling to help a user who is already fully set up —
   * for someone who has not finished onboarding, resend their invitation instead.
   *
   * NOTE: `AuthenticationDialog` performs this same fetch inline; worth collapsing
   * onto this method in a follow-up so there is one path.
   */
  async requestPasswordReset(email: string): Promise<{ success: boolean; message?: string }> {
    const { getServerBaseUrl } = await import('../utils/serverUrl')
    const isDev = !!(import.meta as any).env?.DEV
    const path = '/api/auth/password-reset/request'
    const url = isDev ? path : `${getServerBaseUrl().replace(/\/$/, '')}${path}`

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: isDev ? 'same-origin' : 'include',
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    })

    const result = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(result?.error || 'Failed to request password reset')
    }
    return { success: true, message: result?.message }
  }

  /**
   * Map error codes to fields for UI feedback
   */
  private getErrorField(code: number, errorType?: string): string {
    switch (code) {
      case Parse.Error.INVALID_EMAIL_ADDRESS:
      case Parse.Error.EMAIL_TAKEN:
      case Parse.Error.EMAIL_NOT_FOUND: // Sign-in with an email that has no account
        return 'email'
      case Parse.Error.OBJECT_NOT_FOUND: // Often used for invalid login
        return 'password'
      case Parse.Error.USERNAME_TAKEN:
        return 'username'
      case Parse.Error.OPERATION_FORBIDDEN: // Domain validation errors
        return 'domain'
      case 141: // FUNCTION_NOT_FOUND
        return 'general'
      // Rate limited: neither field is wrong, so it must not fall through to the AUTHENTICATION
      // default below and read as a wrong password.
      case Parse.Error.REQUEST_LIMIT_EXCEEDED:
        return 'general'
      default:
        // Use error type to determine field if code doesn't match
        if (errorType === 'AUTHENTICATION') {
          return 'password'
        }
        return 'general'
    }
  }
}

// Export singleton instance
export const authService = new Back4AppAuthService()
export default authService 
